<template>
  <div :class="['slide-content', `slide-content--${transitionType}`]" :style="styles">
    <slot />
  </div>
</template>

<script>
import SlideContentBlock from './SlideContentBlock.vue'
import {
  setCurrentSlideIndex,
  decreaseCurrentSlideIndex,
  incrementCurrentSlideIndex,
} from './slide-content-helpers'
import { scrollSmoothlyToElement } from '@/helpers/scroll'

export {
  SlideContentBlock,
  setCurrentSlideIndex,
  decreaseCurrentSlideIndex,
  incrementCurrentSlideIndex,
}

const ANIMATION_CALLBACK_TIMEOUT = 2000

export const SLIDE_TRANSITIONS = {
  slideAndAppear: 'slide-and-appear',
  rolling: 'rolling',
}

export const SlideContent = {
  name: 'SlideContent',

  props: {
    currentIndex: {
      type: Number,
      default: 0,
    },

    offset: {
      type: [Number, Function],
      default: 0,
    },

    isOverflowHiddenOnSlide: {
      type: Boolean,
      default: true,
    },

    isAdjustingScroll: {
      type: Boolean,
      default: true,
    },

    hasHeightAnimation: {
      type: Boolean,
      default: true,
    },

    beforeHeightChange: {
      type: Function,
      default: () => {},
    },

    transitionType: {
      type: String,
      default: SLIDE_TRANSITIONS.slideAndAppear,
    },
  },

  data() {
    return {
      animationCallbackTimeout: null,
      currentIndexInternal: -1,
      hasCompletedAnimationOnce: false,
      hasCurrentIndexChangedOnce: false,
      height: '',
      overflow: '',
      previousIndex: -1,
    }
  },

  computed: {
    styles() {
      if (!this.hasHeightAnimation) return {}
      return {
        height: typeof this.height === 'number' ? `${this.height}px` : '',
        overflow: this.overflow,
      }
    },

    isReady() {
      return this.currentIndexInternal >= 0 && this.hasCompletedAnimationOnce
    },
  },

  watch: {
    isReady: {
      immediate: true,
      handler() {
        if (this.isReady) this.$emit('mounted')
      },
    },

    currentIndex() {
      this.hasCurrentIndexChangedOnce = true
      this.goToCurrentIndex()
    },

    height() {
      if (typeof this.height === 'number') this.setAnimationCallbackTimeout()
    },
  },

  async mounted() {
    const currentBlock = await this.getCurrentBlockComponent()
    currentBlock.hasTransition = false
    await this.goToCurrentIndex()
  },

  methods: {
    getDefaultSlots() {
      return (this.$slots.default || []).filter(vNode => vNode.tag)
    },

    async getCurrentBlockHeight() {
      const currentBlock = await this.getCurrentBlockComponent()
      return await currentBlock.getBlockHeight()
    },

    async retry(check, numberOfTimes = 20) {
      let count = numberOfTimes
      let hasPassed = false

      while (!hasPassed && count > 0) {
        hasPassed = check()
        if (!hasPassed) await new Promise(resolve => setTimeout(resolve, 500))

        count--
      }
    },

    async getBlockComponent(index) {
      const blocks = this.getDefaultSlots()
      const vNode = blocks[index] || {}
      if (!vNode.componentInstance) await this.$nextTick()

      this.retry(() => !!vNode.componentInstance)

      return vNode.componentInstance || {}
    },

    async getCurrentBlockComponent() {
      return await this.getBlockComponent(this.currentIndex)
    },

    async goToCurrentIndex() {
      const currentBlock = await this.getCurrentBlockComponent()
      if (!currentBlock || this.currentIndex === this.currentIndexInternal) return

      this.previousIndex = this.currentIndexInternal

      if (this.previousIndex >= 0) {
        const previousBlock = await this.getBlockComponent(this.previousIndex)
        previousBlock.isVisible = true
        this.height = await previousBlock.getBlockHeight?.()
        if (this.isOverflowHiddenOnSlide) this.overflow = 'hidden'
        await this.$nextTick()
      }
      currentBlock.isVisible = true
      const newHeight = await currentBlock.getBlockHeight()
      await this.hideAllOtherBlocks()

      this.adjustScrollPosition()
      this.animateWrapperHeight(newHeight)
      this.currentIndexInternal = this.currentIndex
    },

    async hideAllOtherBlocks() {
      const slots = this.getDefaultSlots()
      for (let index = 0; index < slots.length; index++) {
        if (index === this.currentIndex) continue
        const block = await this.getBlockComponent(index)
        block.isVisible = false
      }
    },

    getOffset() {
      return typeof this.offset === 'function' ? this.offset() : this.offset
    },

    adjustScrollPosition() {
      if (!this.isReady || !this.isAdjustingScroll || !this.hasCurrentIndexChangedOnce) return
      scrollSmoothlyToElement(this.$el, { offset: this.getOffset(), isScrollingUpOnly: true })
    },

    animateWrapperHeight(newHeight) {
      const previousHeight = this.$el.offsetHeight
      this.beforeHeightChange(newHeight, previousHeight)
      this.height = newHeight
    },

    setWrapperHeightAuto() {
      this.height = ''
      this.overflow = ''
    },

    setAnimationCallbackTimeout() {
      clearTimeout(this.animationCallbackTimeout)
      this.animationCallbackTimeout = setTimeout(
        this.setWrapperHeightAuto,
        ANIMATION_CALLBACK_TIMEOUT
      )
    },

    async onBlockAnimationComplete() {
      this.setWrapperHeightAuto()
      this.$emit('animation-complete')

      const currentBlock = await this.getCurrentBlockComponent()
      setTimeout(() => (currentBlock.hasTransition = true), 100)
      this.hasCompletedAnimationOnce = true
    },
  },
}
export default SlideContent
</script>

<style lang="scss" scoped>
@import './slide-content-settings';

.slide-content {
  position: relative;
  transition: height #{$transition-time} #{$transition-cubic};
}
</style>
